import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, KpiCard } from "@/components/Bits";
import { Field, inputCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, inrShort, num, dmy, today } from "@/lib/format";

export default function PharmacyReports() {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState(today());

  const load = useCallback(async () => {
    setD(null);
    try {
      const { data } = await api.get("/pharmacy/reports", { params: { from_date: from || undefined, to_date: to || undefined } });
      setD(data);
    } catch (e) { setError(apiError(e)); }
  }, [from, to]);

  useEffect(() => { if (can("pharmacy.reports.view")) load(); }, [load, can]);
  if (!can("pharmacy.reports.view")) return <NoAccess what="pharmacy reports" />;

  const quick = (days) => {
    setTo(today());
    setFrom(new Date(Date.now() - days * 86400000).toISOString().slice(0, 10));
  };

  const exportCsv = () => {
    const rows = [
      ["Metric", "Value"],
      ["From", d.from], ["To", d.to],
      ["Sales total", d.sales_total], ["Invoices", d.invoice_count], ["Voided", d.void_count],
      ["Purchases", d.purchases_total], ["Expenses", d.expenses_total], ["Returns", d.returns_total],
      ["GST collected", d.gst_collected], ["COGS", d.cogs], ["Gross margin", d.gross_margin],
      [], ["Date", "Sales"], ...d.daily.map((r) => [r.date, r.amount]),
      [], ["Medicine", "Qty", "Amount"], ...d.top_medicines.map((m) => [m.medicine, m.quantity, m.amount]),
    ];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pharmacy-report-${d.from}-to-${d.to}.csv`;
    a.click();
  };

  return (
    <div data-testid="pharmacy-reports-page">
      <PageHeader overline="Pharmacy" title="Reports"
        subtitle="Sales, purchases, expenses, returns, GST and gross margin for any range."
        actions={can("pharmacy.reports.export") && d && (
          <button onClick={exportCsv} className={ghostBtnCls} data-testid="pharmacy-export-csv-btn">Export CSV</button>
        )} />
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <Field label="From"><input type="date" data-testid="pharmacy-report-from" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" data-testid="pharmacy-report-to" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <button onClick={() => quick(0)} className={ghostBtnCls} data-testid="report-daily-btn">Daily</button>
        <button onClick={() => quick(30)} className={ghostBtnCls} data-testid="report-monthly-btn">Monthly</button>
        <button onClick={() => quick(365)} className={ghostBtnCls} data-testid="report-yearly-btn">Yearly</button>
      </div>
      {error && <ErrorState message={error} />}
      {!d ? <Loading /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard label="Sales" value={inrShort(d.sales_total)} hint={`${num(d.invoice_count)} invoices`} tone="accent" to="/app/pharmacy/sales" testid="rep-sales" />
            <KpiCard label="Purchases" value={inrShort(d.purchases_total)} hint={`${num(d.purchase_count)} entries`} to="/app/pharmacy/purchases" testid="rep-purchases" />
            <KpiCard label="Expenses" value={inrShort(d.expenses_total)} to="/app/pharmacy/expenses" testid="rep-expenses" />
            <KpiCard label="Returns" value={inrShort(d.returns_total)} to="/app/pharmacy/returns" testid="rep-returns" />
            <KpiCard label="GST collected" value={inrShort(d.gst_collected)} onClick={() => {}} testid="rep-gst" />
            <KpiCard label="Gross margin" value={inrShort(d.gross_margin)} hint={`COGS ${inrShort(d.cogs)}`} tone="success" onClick={() => {}} testid="rep-margin" />
            <KpiCard label="Low stock" value={num(d.low_stock_count)} tone="warning" to="/app/pharmacy/inventory?filter=low_stock" testid="rep-low-stock" />
            <KpiCard label="Expiring / expired" value={`${num(d.expiring_count)} / ${num(d.expired_count)}`} tone="danger" to="/app/pharmacy/inventory?filter=expiring" testid="rep-expiry" />
          </div>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <div className="panel">
              <div className="px-6 py-5 border-b border-slate-200"><h3 className="font-ui font-bold tracking-tight">Daily sales</h3></div>
              <div className="p-6 space-y-2 text-sm max-h-80 overflow-y-auto">
                {d.daily.length === 0 ? <p className="text-slate-400">No sales in this range.</p> :
                  d.daily.map((r) => <div key={r.date} className="flex justify-between"><span className="text-slate-600">{dmy(r.date)}</span><span className="font-medium">{inr(r.amount)}</span></div>)}
              </div>
            </div>
            <div className="panel">
              <div className="px-6 py-5 border-b border-slate-200"><h3 className="font-ui font-bold tracking-tight">Top medicines</h3></div>
              <div className="p-6 space-y-2 text-sm max-h-80 overflow-y-auto">
                {d.top_medicines.length === 0 ? <p className="text-slate-400">No sales in this range.</p> :
                  d.top_medicines.map((m) => (
                    <div key={m.medicine} className="flex justify-between">
                      <span className="text-slate-600">{m.medicine} <span className="text-xs text-slate-400">× {m.quantity}</span></span>
                      <span className="font-medium">{inr(m.amount)}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          {d.void_count > 0 && (
            <p className="mt-6 text-sm text-slate-500">
              {d.void_count} invoice(s) voided in this range — see <Link to="/app/audit" className="underline">audit log</Link>.
            </p>
          )}
        </>
      )}
    </div>
  );
}
